import { contactKeys, entryKeys, type PostEntryInput } from '@taler/api-client'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { problemDetail } from '../internal/problem.ts'

export interface UseContactPaybackOptions {
  readonly ledgerId: () => string | undefined
  readonly contactId: () => string
}

export interface UseContactPayback {
  readonly isSubmitting: ComputedRef<boolean>
  readonly hasError: ComputedRef<boolean>
  readonly errorDetail: ComputedRef<string | undefined>
  submit(input: PostEntryInput): Promise<void>
  reset(): void
}

export function useContactPayback(options: UseContactPaybackOptions): UseContactPayback {
  const api = useApi()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (input: PostEntryInput) => api.entries.create(options.ledgerId()!, input),
    onSuccess: async () => {
      const ledgerId = options.ledgerId() ?? ''
      await Promise.all([
        ...entryKeys
          .invalidatedByWrite(ledgerId)
          .map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        queryClient.invalidateQueries({ queryKey: contactKeys.balances(ledgerId, options.contactId()) }),
      ])
    },
  })

  return {
    isSubmitting: computed(() => mutation.isPending.value),
    hasError: computed(() => mutation.error.value !== null),
    errorDetail: computed(() => problemDetail(mutation.error.value)),
    submit: (input) => mutation.mutateAsync(input).then(() => undefined),
    reset: () => mutation.reset(),
  }
}
